import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { BASIS, EXTRA } from "./mboxVars";

// Polling alle 2 s (Takt, Schutz gegen parallele Abfragen, Zeitlimit wie im Original)
export function useMBox(deviceId, seite) {
  const [W, setW] = useState({});
  const [verbindung, setVerbindung] = useState({ ok: false, text: "verbinde …", detail: "" });
  const [info, setInfo] = useState(null);
  const laeuft = useRef(false);
  const seiteRef = useRef(seite);
  seiteRef.current = seite;

  const gestoppt = useRef(false); // nach 403/404 nicht weiter pollen (Zugriff fehlt / Geraet weg)

  const aktualisieren = useCallback(async () => {
    if (laeuft.current || !deviceId || gestoppt.current) return;
    laeuft.current = true;
    try {
      const r = await api.post(`/mbox/${deviceId}/lesen`, { namen: [...BASIS, ...(EXTRA[seiteRef.current] || [])] }, { timeout: 30000 });
      if (!r.data.ok) {
        setVerbindung({ ok: false, text: "keine Verbindung", detail: r.data.fehler || "" });
        return;
      }
      setW(prev => ({ ...prev, ...r.data.werte }));
      setVerbindung({ ok: true, text: "Stand " + new Date().toLocaleTimeString("de-DE"), detail: "letzte erfolgreiche Abfrage" });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      const st = e.response?.status;
      if (st === 403 || st === 404) gestoppt.current = true;
      setVerbindung({ ok: false, text: st === 403 ? "kein Zugriff" : st === 404 ? "Gerät nicht gefunden" : "Dienst nicht erreichbar", detail: msg });
    } finally {
      laeuft.current = false;
    }
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId) return;
    gestoppt.current = false;
    let t = null;
    const lade = () => api.get(`/mbox/${deviceId}/status`).then(r => setInfo(r.data)).catch(e => {
      if (e.response?.status === 403 || e.response?.status === 404) { gestoppt.current = true; if (t) clearInterval(t); }
      setInfo(prev => prev && !prev.error ? prev : { error: e.response?.data?.detail || "Status nicht ladbar", level: "none" });
    });
    lade();
    t = setInterval(lade, 10000);
    return () => clearInterval(t);
  }, [deviceId]);

  useEffect(() => {
    aktualisieren();
    const t = setInterval(aktualisieren, 2000);
    const vis = () => { if (!document.hidden) aktualisieren(); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [aktualisieren]);

  useEffect(() => { aktualisieren(); }, [seite, aktualisieren]);

  const post = useCallback(async (pfad, body, params) => {
    try {
      const r = await api.post(`/mbox/${deviceId}/${pfad}`, body ?? {}, { params, timeout: 30000 });
      return r.data;
    } catch (e) {
      return { ok: false, fehler: e.response?.data?.detail || e.message };
    }
  }, [deviceId]);

  const befehl = useCallback(async (name, index) => {
    const r = await post("befehl/" + name, undefined, index !== undefined && index !== null ? { index } : undefined);
    if (!r.ok) { toast.error(r.fehler); return false; }
    const fe = W["DB_HMI.Status.Fern_Erlaubt"], wm = W["DB_HMI.Status.Wartungsmodus"];
    const gesperrt = fe === false || wm === true;
    if (gesperrt) toast.warning("gesendet – wird aber ignoriert (Fernbedienung gesperrt)");
    else toast.success("gesendet");
    setTimeout(aktualisieren, 400);
    return true;
  }, [post, W, aktualisieren]);

  return { W, verbindung, info, aktualisieren, post, befehl };
}
